import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Skeleton,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import ShieldIcon from '@mui/icons-material/Shield';
import { Link } from 'react-router-dom';
import NavigationBar from '../components/NavigationBar';
import { getProductosPaged } from '../services/modules/Productos';
import { BRANDING } from '../config/branding';
import heroTecno from '../assets/landing-hero.jpg';
import heroDebians from '../assets/Logo.png';
import FloatingTelegramButton from '../components/FloatingTelegramButton';

const highlights = [
  {
    title: 'Ventas y cobranza',
    description: 'Gestiona compras, ventas y abonos desde un mismo panel.',
    icon: <BoltIcon color="primary" />,
  },
  {
    title: 'Control de reparaciones',
    description: 'Seguimiento visual del estado de cada equipo ingresado.',
    icon: <ShieldIcon color="primary" />,
  },
  {
    title: 'Reportería moderna',
    description: 'KPIs con filtros por fecha para tomar decisiones rápidas.',
    icon: <CheckCircleIcon color="primary" />,
  },
];

const fallbackImage = 'https://via.placeholder.com/320x220.png?text=Producto';
const currencyGTQ = new Intl.NumberFormat('es-GT', {
  style: 'currency',
  currency: 'GTQ',
  minimumFractionDigits: 2,
});

const upgradeToHttpsIfNeeded = (value) => {
  const url = String(value || '');
  if (!url) return url;
  if (typeof window === 'undefined') return url;
  if (window.location?.protocol !== 'https:') return url;
  return url.startsWith('http://') ? `https://${url.slice('http://'.length)}` : url;
};

const getUrlBases = (rawInput) => {
  const raw = String(rawInput || '').trim();
  if (!raw) return { origin: '', base: '' };
  const noTrail = raw.replace(/\/$/, '');
  try {
    const url = new URL(noTrail);
    const basePath = String(url.pathname || '').replace(/\/$/, '');
    return { origin: url.origin, base: `${url.origin}${basePath}` };
  } catch {
    return { origin: noTrail, base: noTrail };
  }
};

const resolveCatalogImage = (value) => {
  if (!value) return fallbackImage;
  if (/^https?:\/\//i.test(value)) return upgradeToHttpsIfNeeded(value);
  const { origin: apiOrigin, base: apiBase } = getUrlBases(BRANDING.apiUrl);
  const { origin: uploadOrigin, base: uploadBase } = getUrlBases(BRANDING.uploadUrl || BRANDING.apiUrl);
  const normalized = value.startsWith('/') ? value : `/${value}`;
  const isUpload = normalized.startsWith('/uploads/') || normalized.startsWith('/upload/');
  const outBase = isUpload ? (uploadOrigin || uploadBase) : (apiBase || apiOrigin);
  const out = outBase ? `${outBase}${normalized}` : normalized;
  return upgradeToHttpsIfNeeded(out);
};

const HERO_ASSETS = {
  'landing-hero.jpg': heroTecno,
  'Logo.png': heroDebians,
};

const LandingPage = () => {
  const [featured, setFeatured] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [featuredError, setFeaturedError] = useState(null);

  useEffect(() => {
    const loadFeatured = async () => {
      setFeaturedLoading(true);
      setFeaturedError(null);
      try {
        const response = await getProductosPaged({ page: 0, pageSize: 8 });
        setFeatured(Array.isArray(response?.items) ? response.items : []);
      } catch (error) {
        console.error('No se pudo cargar productos destacados', error);
        setFeaturedError('No pudimos cargar los productos en este momento.');
      } finally {
        setFeaturedLoading(false);
      }
    };

    loadFeatured();
  }, []);

  return (
  <>
    <NavigationBar />
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #180342 0%, #3f0f88 50%, #621fd1 100%)',
        color: 'white',
        pb: 10,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.12), transparent 45%), radial-gradient(circle at 80% 0%, rgba(0,255,255,0.2), transparent 25%)',
          opacity: 0.8,
        }}
      />

      <Container maxWidth="lg" sx={{ pt: 10, position: 'relative', zIndex: 1 }}>
        {(() => {
          const isLogo = BRANDING.landingHeroAsset === 'Logo.png';
          const heroSrc = HERO_ASSETS[BRANDING.landingHeroAsset] || heroTecno;
          return (
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Chip
                label="Tecnología + Retail + Servicios"
                sx={{
                  width: 'fit-content',
                  bgcolor: 'rgba(0,212,255,0.18)',
                  color: 'white',
                  fontWeight: 600,
                  letterSpacing: 0.8,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  textTransform: 'uppercase',
                  textShadow: '0 4px 18px rgba(0,0,0,0.45)',
                }}
              >
                {BRANDING.companyName}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                La tecnología que hará despegar tu negocio.
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 520 }}>
                Desde inventarios inteligentes hasta reportes con data en tiempo real. Todo en una
                experiencia visual inspirada en el metaverso para enamorar a tus clientes y equipo.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    borderRadius: 999,
                    px: 4,
                    py: 1.5,
                    backgroundImage: 'linear-gradient(90deg, #00d4ff, #7c3aed)',
                  }}
                  component={Link}
                  to="/catalogo"
                >
                  Explorar catálogo
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderRadius: 999,
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.5)',
                    px: 4,
                    py: 1.5,
                  }}
                  href="/login"
                >
                  Entrar al panel
                </Button>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 6,
                p: 2,
                background: 'linear-gradient(135deg, rgba(0,212,255,0.55), rgba(124,58,237,0.45))',
                boxShadow: '0 30px 80px rgba(11,16,58,0.8)',
              }}
            >
              <Box
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: '4px solid rgba(255,255,255,0.4)',
                }}
              >
                <Box
                  component="img"
                  src={heroSrc}
                  alt={BRANDING.companyName}
                  sx={{
                    width: '100%',
                    display: 'block',
                    objectFit: isLogo ? 'contain' : 'cover',
                    bgcolor: isLogo ? 'rgba(2,6,23,0.35)' : 'transparent',
                    p: isLogo ? { xs: 5, sm: 6 } : 0,
                    maxHeight: isLogo ? { xs: 360, md: 420 } : 460,
                  }}
                />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 16,
                  borderRadius: 4,
                  border: '2px solid rgba(255,255,255,0.3)',
                  pointerEvents: 'none',
                }}
              />
            </Box>
          </Grid>
        </Grid>
          );
        })()}

        <Grid container spacing={3} sx={{ mt: 8 }}>
          {highlights.map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  bgcolor: 'rgba(10,16,64,0.65)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
                }}
              >
                <CardContent>
                  <Stack spacing={1.5}>
                    <Box>{item.icon}</Box>
                    <Typography variant="h6" fontWeight={700}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {item.description}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 10 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Productos destacados
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 720 }}>
                Una vitrina rápida para que tus clientes vean lo mejor sin tener que abrir todo el catálogo.
              </Typography>
            </Box>
            <Button
              component={Link}
              to="/catalogo"
              variant="outlined"
              sx={{ borderRadius: 999, color: 'white', borderColor: 'rgba(255,255,255,0.45)' }}
            >
              Ver catálogo completo
            </Button>
          </Stack>

          {featuredError && (
            <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.75)' }}>{featuredError}</Typography>
          )}

          <Box
            sx={{
              mt: 3,
              display: 'flex',
              gap: 3,
              overflowX: 'auto',
              pb: 1.5,
              scrollSnapType: 'x mandatory',
              '& > *': { scrollSnapAlign: 'start' },
            }}
          >
            {featuredLoading
              ? Array.from({ length: 6 }).map((_, index) => (
                  <Card
                    key={`featured-skeleton-${index}`}
                    sx={{
                      minWidth: 260,
                      borderRadius: 4,
                      bgcolor: 'rgba(10,16,64,0.55)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    <Skeleton variant="rectangular" height={160} />
                    <CardContent>
                      <Stack spacing={1}>
                        <Skeleton width="60%" />
                        <Skeleton width="90%" />
                        <Skeleton width="45%" height={30} />
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              : featured.map((product) => (
                  <Card
                    key={product.id}
                    sx={{
                      minWidth: 260,
                      borderRadius: 4,
                      bgcolor: 'rgba(10,16,64,0.65)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      overflow: 'hidden',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
                      transition: 'transform 180ms ease, box-shadow 180ms ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 18px 60px rgba(0,0,0,0.5)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={resolveCatalogImage(product.imagen_url)}
                      alt={product.descripcion}
                      loading="lazy"
                      sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                    />
                    <CardContent>
                      <Stack spacing={1}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                          {product.categoria || 'General'}
                        </Typography>
                        <Typography fontWeight={800}>{product.descripcion}</Typography>
                        <Typography sx={{ color: '#8be9fd', fontWeight: 800 }}>
                          {currencyGTQ.format(Number(product.precio_venta) || 0)}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
          </Box>
        </Box>
      </Container>
      <FloatingTelegramButton />
    </Box>
  </>
  );
};

export default LandingPage;
